/**
 * ============================================================================
 * PAYMENT SUCCESS PAGE - 100% Ready
 * ============================================================================
 * 
 * Page for displaying payment success with verification
 * Uses componentShowcase patterns and styles
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { UserDashboardLayout } from '../components/user';
import { usePayment } from '../hooks/usePayment';
import { useTransaction } from '../hooks/useTransaction';
import { useToast } from '../providers';
import { useI18n } from '../providers';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import Spinner from '../components/common/Spinner';
import StatusBadge from '../components/common/StatusBadge';
import '../styles/animations.css';
import '../styles/global.css';

/**
 * Payment Success Page
 * @returns {JSX.Element}
 */
const PaymentSuccess = () => {
  const { transactionId, paymentId } = useParams();
  const navigate = useNavigate();
  const { getPayment, getPaymentStatus, verifyPayment, loading } = usePayment();
  const { getTransaction } = useTransaction();
  const { showError, showSuccess } = useToast();
  const { formatCurrency, formatDate, language } = useI18n();

  const [payment, setPayment] = useState(null);
  const [transaction, setTransaction] = useState(null);
  const [isVerifying, setIsVerifying] = useState(true);
  const [verificationAttempts, setVerificationAttempts] = useState(0);
  const maxVerificationAttempts = 5;

  useEffect(() => {
    const verifyPaymentStatus = async () => {
      if (!paymentId && !transactionId) {
        setIsVerifying(false);
        return;
      }

      try {
        setIsVerifying(true);
        let paymentData = null;

        // If paymentId is provided, get payment directly
        if (paymentId) {
          paymentData = await getPayment(paymentId);
        } else if (transactionId) {
          // Get transaction first, then get payment
          const txData = await getTransaction(transactionId);
          setTransaction(txData);
          if (txData.paymentId || txData.payments?.[0]?.id) {
            const pid = txData.paymentId || txData.payments?.[0]?.id;
            paymentData = await getPayment(pid);
          }
        }

        if (paymentData) {
          setPayment(paymentData);

          // If payment is still pending, poll for status
          if (paymentData.status === 'pending' || paymentData.status === 'processing') {
            if (verificationAttempts < maxVerificationAttempts) {
              setTimeout(() => {
                setVerificationAttempts((prev) => prev + 1);
                verifyPaymentStatus();
              }, 3000); // Poll every 3 seconds
            } else {
              setIsVerifying(false);
            }
          } else {
            setIsVerifying(false);
            if (paymentData.status === 'completed') {
              showSuccess(
                language === 'fr' ? 'Paiement confirmé avec succès!' : 'Payment confirmed successfully!'
              );
            }
          }
        } else {
          setIsVerifying(false);
        }
      } catch (error) {
        console.error('Payment verification error:', error);
        setIsVerifying(false);
        // Don't show error if payment might still be processing
        if (verificationAttempts >= maxVerificationAttempts) {
          showError(
            error.message || (language === 'fr' ? 'Échec de la vérification du paiement' : 'Failed to verify payment')
          );
        }
      }
    };

    verifyPaymentStatus();
  }, [paymentId, transactionId, getPayment, getTransaction, verificationAttempts, showError, showSuccess, language]);

  const getStatusVariant = (status) => {
    const variants = {
      pending: 'warning',
      processing: 'primary',
      completed: 'success',
      failed: 'error',
      cancelled: 'default',
    };
    return variants[status] || 'default';
  };

  const getPaymentIcon = (method) => {
    const icons = {
      card: '💳',
      mtn_mobile_money: '📱',
      orange_money: '🍊',
      mpesa: '📱',
      bank_transfer: '🏦',
    };
    return icons[method] || '💰';
  };

  const getPaymentMethodName = (method) => {
    const names = {
      card: language === 'fr' ? 'Carte bancaire' : 'Card',
      mtn_mobile_money: 'MTN Mobile Money',
      orange_money: 'Orange Money',
      mpesa: 'M-Pesa',
      bank_transfer: language === 'fr' ? 'Virement bancaire' : 'Bank Transfer',
    };
    return names[method] || method;
  };

  if (isVerifying && !payment) {
    return (
      <UserDashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <Spinner size="lg" />
            <p className="mt-4 text-gray-600 dark:text-gray-400">
              {language === 'fr' ? 'Vérification du paiement...' : 'Verifying payment...'}
            </p>
          </div>
        </div>
      </UserDashboardLayout>
    );
  }

  return (
    <UserDashboardLayout>
      <div className="max-w-2xl mx-auto">
        {/* Success Header */}
        <div className="text-center mb-8 animate-fadeInUp">
          <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center text-5xl shadow-lg shadow-green-500/30 animate-float">
            ✅
          </div>
          <h1 className="text-4xl font-black bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent mb-2">
            {language === 'fr' ? 'Paiement Réussi!' : 'Payment Successful!'}
          </h1>
          <p className="text-gray-600 dark:text-gray-400 text-lg">
            {language === 'fr'
              ? 'Votre paiement a été traité avec succès'
              : 'Your payment has been processed successfully'}
          </p>
        </div>

        {/* Payment Details Card */}
        {payment && (
          <Card
            variant="glass"
            className="backdrop-blur-xl border-white/20 dark:border-white/10 mb-6 animate-fadeInUp"
            style={{ animationDelay: '0.2s' }}
            hover
          >
            <div className="space-y-6">
              {/* Payment Info */}
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center text-3xl shadow-lg shadow-green-500/30 animate-float">
                  {getPaymentIcon(payment.paymentMethod || payment.method)}
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-black text-gray-900 dark:text-white mb-1">
                    {language === 'fr' ? 'Paiement' : 'Payment'} #{payment.id?.slice(0, 8) || 'N/A'}
                  </h3>
                  <StatusBadge variant={getStatusVariant(payment.status)} size="lg">
                    {payment.status}
                  </StatusBadge>
                </div>
              </div>

              {/* Amount */}
              <div className="p-4 bg-gradient-to-r from-green-50/80 to-emerald-50/80 dark:from-green-900/20 dark:to-emerald-900/20 rounded-xl backdrop-blur-sm">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
                  {language === 'fr' ? 'Montant Payé' : 'Amount Paid'}
                </p>
                <p className="text-3xl font-black text-gray-900 dark:text-white">
                  {formatCurrency(payment.amount || 0, payment.currency || 'XAF')}
                </p>
              </div>

              {/* Details Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card variant="outlined" className="p-4">
                  <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                    {language === 'fr' ? 'Méthode de Paiement' : 'Payment Method'}
                  </p>
                  <p className="text-sm font-bold text-gray-900 dark:text-white">
                    {getPaymentMethodName(payment.paymentMethod || payment.method)}
                  </p>
                </Card>
                {payment.gatewayReference && (
                  <Card variant="outlined" className="p-4">
                    <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                      {language === 'fr' ? 'Référence' : 'Reference'}
                    </p>
                    <p className="text-sm font-bold text-gray-900 dark:text-white">
                      {payment.gatewayReference}
                    </p>
                  </Card>
                )}
                {payment.completedAt && (
                  <Card variant="outlined" className="p-4">
                    <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                      {language === 'fr' ? 'Complété le' : 'Completed At'}
                    </p>
                    <p className="text-sm font-bold text-gray-900 dark:text-white">
                      {formatDate(payment.completedAt)}
                    </p>
                  </Card>
                )}
                {payment.transactionId && (
                  <Card variant="outlined" className="p-4">
                    <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                      {language === 'fr' ? 'Transaction' : 'Transaction'}
                    </p>
                    <p className="text-sm font-bold text-gray-900 dark:text-white">
                      #{payment.transactionId?.slice(0, 8) || 'N/A'}
                    </p>
                  </Card>
                )}
              </div>
            </div>
          </Card>
        )}

        {/* Transaction Info */}
        {transaction && (
          <Card
            variant="glass"
            className="backdrop-blur-xl border-white/20 dark:border-white/10 mb-6 animate-fadeInUp"
            style={{ animationDelay: '0.3s' }}
            hover
          >
            <h3 className="text-lg font-black text-gray-900 dark:text-white mb-4">
              {language === 'fr' ? 'Détails de la Transaction' : 'Transaction Details'}
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {language === 'fr' ? 'Titre' : 'Title'}
                </span>
                <span className="text-sm font-bold text-gray-900 dark:text-white">
                  {transaction.title}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {language === 'fr' ? 'Montant' : 'Amount'}
                </span>
                <span className="text-sm font-bold text-gray-900 dark:text-white">
                  {formatCurrency(transaction.amount || 0, transaction.currency || 'XAF')}
                </span>
              </div>
              {transaction.status && (
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    {language === 'fr' ? 'Statut' : 'Status'}
                  </span>
                  <StatusBadge variant={getStatusVariant(transaction.status)} size="sm">
                    {transaction.status}
                  </StatusBadge>
                </div>
              )}
            </div>
          </Card>
        )}

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-4 animate-fadeInUp" style={{ animationDelay: '0.4s' }}>
          {transactionId && (
            <Button
              variant="gradient"
              onClick={() => navigate(`/transactions/${transactionId}`)}
              className="flex-1 bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 shadow-xl hover:shadow-2xl transition-all"
            >
              👁️ {language === 'fr' ? 'Voir la Transaction' : 'View Transaction'}
            </Button>
          )}
          <Button
            variant="outline"
            onClick={() => navigate('/dashboard')}
            className="flex-1"
          >
            {language === 'fr' ? 'Retour au Tableau de Bord' : 'Back to Dashboard'}
          </Button>
        </div>

        {/* Next Steps */}
        <Card
          variant="glass"
          className="backdrop-blur-xl border-white/20 dark:border-white/10 mt-6 p-6 bg-gradient-to-r from-blue-50/50 to-green-50/50 dark:from-blue-900/10 dark:to-green-900/10 animate-fadeInUp"
          style={{ animationDelay: '0.5s' }}
        >
          <h3 className="text-lg font-black text-gray-900 dark:text-white mb-3">
            {language === 'fr' ? 'Prochaines Étapes' : 'Next Steps'}
          </h3>
          <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
            <li className="flex items-start gap-2">
              <span className="text-green-500 mt-1">✓</span>
              <span>
                {language === 'fr'
                  ? 'Les fonds sont maintenant sécurisés en escrow'
                  : 'Funds are now secured in escrow'}
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-500 mt-1">→</span>
              <span>
                {language === 'fr'
                  ? 'Le vendeur sera notifié et commencera le travail'
                  : 'The seller will be notified and start work'}
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-purple-500 mt-1">📧</span>
              <span>
                {language === 'fr'
                  ? 'Vous recevrez des notifications par email et SMS'
                  : 'You will receive email and SMS notifications'}
              </span>
            </li>
          </ul>
        </Card>
      </div>
    </UserDashboardLayout>
  );
};

export default PaymentSuccess;
